const React = require("react")
const Polyglot = require("node-polyglot")
const PropTypes = require("prop-types")

const NO_POLYGLOT_CONTEXT =
  "Warning: t is called without Polyglot context. Perhaps you need to wrap the component in <I18n>?"
const ELEMENT_IDENTIFIER = /<(\d+)\/>/

const warnWithoutContext = key => {
  if (process.env.NODE_ENV !== "production") {
    console.error(NO_POLYGLOT_CONTEXT)
  }

  return key
}

const I18nContext = React.createContext({
  locale: undefined,
  t: warnWithoutContext
})

const enhanceT = originalT => {
  function t(key, interpolations) {
    if (interpolations === undefined || typeof interpolations === "number") {
      return originalT(key, interpolations)
    }

    const elements = []
    const polyglotInterpolations = { ...interpolations }

    Object.keys(polyglotInterpolations).forEach(interpolationKey => {
      if (React.isValidElement(polyglotInterpolations[interpolationKey])) {
        elements.push(polyglotInterpolations[interpolationKey])
        polyglotInterpolations[interpolationKey] = `<${elements.length - 1}/>`
      }
    })

    const translated = originalT(key, polyglotInterpolations)

    if (!elements.length) {
      return translated
    }

    const chunks = translated.split(ELEMENT_IDENTIFIER)
    const rendered = chunks.splice(0, 1)

    while (chunks.length) {
      const [index, trailingText] = chunks.splice(0, 2)
      const elementIndex = Number.parseInt(index, 10)
      const element = elements[elementIndex]

      rendered.push(React.cloneElement(element, { key: elementIndex }, element.props.children))
      if (trailingText) rendered.push(trailingText)
    }

    return rendered
  }

  return t
}

function I18n({ allowMissing, children, interpolation, locale, onMissingKey, phrases, pluralRules }) {
  const polyglot = React.useMemo(
    () =>
      new Polyglot({
        allowMissing,
        interpolation,
        locale,
        onMissingKey,
        phrases,
        pluralRules
      }),
    [allowMissing, interpolation, locale, onMissingKey, phrases, pluralRules]
  )
  const value = React.useMemo(
    () => ({
      locale: polyglot.locale(),
      t: enhanceT(polyglot.t.bind(polyglot))
    }),
    [polyglot]
  )

  return React.createElement(I18nContext.Provider, { value }, value.locale && children)
}

const useLocale = () => React.useContext(I18nContext).locale
const useT = () => React.useContext(I18nContext).t

function T({ count, fallback, interpolations, phrase }) {
  const t = useT()
  const options = {
    ...(fallback && { _: fallback }),
    ...(count && { smart_count: count }),
    ...interpolations
  }

  return t(phrase, options)
}

I18n.propTypes = {
  allowMissing: PropTypes.bool,
  children: PropTypes.node.isRequired,
  interpolation: PropTypes.shape({}),
  locale: PropTypes.string.isRequired,
  onMissingKey: PropTypes.func,
  phrases: PropTypes.shape({}).isRequired,
  pluralRules: PropTypes.shape({})
}

I18n.defaultProps = {
  allowMissing: undefined,
  interpolation: undefined,
  onMissingKey: undefined,
  pluralRules: undefined
}

T.propTypes = {
  count: PropTypes.number,
  fallback: PropTypes.string,
  interpolations: PropTypes.shape({}),
  phrase: PropTypes.string.isRequired
}

module.exports = { I18n, T, useLocale, useT }
